import {Component, Input} from '@angular/core';
import {
    FaqStatus,
    FraudReportStatus,
    MsisdnStatus,
    RefundStatus,
    ResolutionStatus,
    TransactionCompletionStatus,
    TrendStatus,
} from '@npck/af-frontend-api';
import {RefundStatuses, RefundStatusExtended} from '../../pages/refunds/refunds.component';

@Component({
    selector: 'af-status',
    templateUrl: 'status.component.html',
    styleUrls: ['status.component.scss'],
    standalone: false,
})
export class StatusComponent {
    @Input() statusText: string;
    @Input() icon: string;
    @Input() status: StatusStyle;
    @Input() textSize: 't4' | 't6' = 't6';
}

export enum StatusStyle {
    SUCCESS = 'SUCCESS',
    SUCCESS_DARK = 'SUCCESS_DARK',
    ERROR = 'ERROR',
    INACTIVE = 'INACTIVE',
    INACTIVE_LIGHT = 'INACTIVE_LIGHT',
    PENDING = 'PENDING',
    WARNING = 'WARNING',
    WARNING_DARK = 'WARNING_DARK',
}

export function isFinalStatus(status: string): boolean {
    return ['Мошенничество', 'Не мошенничество'].includes(status);
}

export function isWaitingStatus(status: string): boolean {
    return status === 'Ожидание дополнения';
}

export function shouldUseAllStatuses(status: string): boolean {
    return ['Дополнено', 'Предотвращено', 'Мошенничество'].includes(status);
}

export function mapResolutionStatusToStyle(status: ResolutionStatus): StatusStyle {
    switch (status) {
        case ResolutionStatus.R:
            return StatusStyle.INACTIVE;
        case ResolutionStatus.NF:
        case ResolutionStatus.NOT_CONFIRMED:
            return StatusStyle.SUCCESS;
        case ResolutionStatus.P:
            return StatusStyle.WARNING;
        case ResolutionStatus.F:
        case ResolutionStatus.CONFIRMED:
            return StatusStyle.ERROR;
        default:
            return null;
    }
}

export function isFinalResolutionStatus(status: ResolutionStatus): boolean {
    return status === ResolutionStatus.F || status === ResolutionStatus.NF;
}

export function mapIncidentStatusToStyle(status: string): StatusStyle {
    switch (status) {
        case 'Зарегистрирован':
        case 'Ожидание дополнения':
            return StatusStyle.INACTIVE;
        case 'Не мошенничество':
        case 'Не подтвержден':
        case 'Дополнено':
        case 'Не требует дополнения':
            return StatusStyle.SUCCESS;
        case 'В работе':
        case 'Предотвращено':
            return StatusStyle.WARNING;
        case 'Мошенничество':
        case 'Подтвержден':
        case 'Удален':
            return StatusStyle.ERROR;
        default:
            return null;
    }
}

export function mapTransactionStatusToStyle(status: string): StatusStyle {
    switch (status) {
        case 'Попытка':
            return StatusStyle.WARNING;
        case 'Заблокирована':
            return StatusStyle.ERROR;
        case 'Совершена':
            return StatusStyle.SUCCESS;
        default:
            return null;
    }
}

export function mapTransactionCompletionStatusToStyle(completionStatus: TransactionCompletionStatus): StatusStyle {
    switch (completionStatus) {
        case TransactionCompletionStatus.PREVENTED:
            return StatusStyle.WARNING;
        case TransactionCompletionStatus.NO_COMPLETION_REQUIRED:
            return StatusStyle.INACTIVE;
        case TransactionCompletionStatus.AWAITING_COMPLETION:
            return StatusStyle.PENDING;
        case TransactionCompletionStatus.COMPLETED:
            return StatusStyle.SUCCESS;
        default:
            return null;
    }
}

export function mapRefundStatusToStyle(refundStatus: RefundStatuses): StatusStyle {
    switch (refundStatus) {
        case RefundStatus.DRAFT:
            return StatusStyle.INACTIVE;
        case RefundStatus.IN_EXECUTION:
            return StatusStyle.PENDING;
        case RefundStatus.PENDING_CONFIRMATION:
            return StatusStyle.WARNING;
        case RefundStatus.PENDING_APPROVAL:
            return StatusStyle.WARNING;
        case RefundStatus.EXECUTED:
            return StatusStyle.SUCCESS;
        case RefundStatus.DELETED:
            return StatusStyle.ERROR;
        case RefundStatusExtended.REJECTED_BY_PROSECUTOR:
        case RefundStatusExtended.REJECTED_BY_SENDER:
        case RefundStatusExtended.REJECTED_BY_RECEIVER:
            return StatusStyle.ERROR;
        default:
            return null;
    }
}

export function mapMsisdnStatusToStyle(status: string): StatusStyle {
    switch (status) {
        case MsisdnStatus.ACTIVE:
            return StatusStyle.SUCCESS;
        case MsisdnStatus.BLOCKED:
            return StatusStyle.ERROR;
        case MsisdnStatus.TERMINATED:
            return StatusStyle.INACTIVE;
        case MsisdnStatus.REISSUED:
            return StatusStyle.WARNING;
        case MsisdnStatus.UNKNOWN:
            return StatusStyle.INACTIVE;
        default:
            return null;
    }
}

export function mapFaqStatusToStyle(status: FaqStatus): StatusStyle {
    switch (status) {
        case FaqStatus.PUBLISHED:
            return StatusStyle.SUCCESS;
        case FaqStatus.NEW:
            return StatusStyle.WARNING;
        case FaqStatus.ANSWERED:
            return StatusStyle.ERROR;
        default:
            return StatusStyle.INACTIVE;
    }
}

export function mapTrendStatusToStyle(trendStatus: TrendStatus, isDeleted: boolean = false): StatusStyle {
    if (isDeleted) {
        return StatusStyle.INACTIVE_LIGHT;
    }

    switch (trendStatus) {
        case TrendStatus.DRAFT:
            return StatusStyle.PENDING;
        case TrendStatus.CONFIRMED:
            return StatusStyle.SUCCESS;
        case TrendStatus.REJECTED:
            return StatusStyle.ERROR;
        case TrendStatus.POSTPONED:
            return StatusStyle.WARNING;
        case TrendStatus.ARCHIVE:
            return StatusStyle.INACTIVE;
        default:
            return null;
    }
}

export function mapFraudReportStatusToStyle(status: FraudReportStatus): StatusStyle {
    switch (status) {
        case FraudReportStatus.GENERATED:
            return StatusStyle.PENDING;
        case FraudReportStatus.REGENERATED:
            return StatusStyle.WARNING;
        case FraudReportStatus.CONFIRMED_SENT:
            return StatusStyle.SUCCESS;
        default:
            return StatusStyle.INACTIVE;
    }
}
